import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteClassSession } from "@/hooks/api/useClassSessions"
import type { DeleteClassSessionProps } from "@/types/class_session"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteClassSession = memo(
  ({ class_session, onSuccess, onCancel }: DeleteClassSessionProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteClassSessionMutation = useDeleteClassSession()

    const displayLabel = class_session.class_template_id ?? class_session.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteClassSessionMutation.mutateAsync(class_session.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting classSession:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete classSession: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete ClassSession"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This classSession will be
                permanently removed.
              </Text>
            </>
          }
          confirmText="Delete ClassSession"
          confirmColorScheme="red"
          isLoading={deleteClassSessionMutation.isPending}
        />
      </>
    )
  },
)

DeleteClassSession.displayName = "DeleteClassSession"

export default DeleteClassSession
export type { DeleteClassSessionProps }
